import axios, { AxiosInstance } from "axios";

// Base URL - Use API Gateway like all other services
const API_BASE_URL =
  import.meta.env.VITE_RIDER_API_URL || "http://localhost:8000/api";

class CommissionService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem("authToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error(
          "Commission service error:",
          error.response?.data || error.message
        );
        return Promise.reject(error);
      }
    );
  }

  // Get all commissions with filters
  async getCommissions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    milestone?: string;
    vendorId?: string;
    telecallerId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const response = await this.api.get("/riders/commissions", { params });
    return response.data;
  }

  // Get commission by ID
  async getCommissionById(id: string) {
    const response = await this.api.get(`/riders/commissions/${id}`);
    return response.data;
  }

  // Get vendor commission summary
  async getVendorCommissionSummary(vendorId: string) {
    const response = await this.api.get(
      `/riders/commissions/vendor/${vendorId}/summary`
    );
    return response.data;
  }

  // Approve commission
  async approveCommission(id: string, approvedBy: string) {
    const response = await this.api.post(`/riders/commissions/${id}/approve`, {
      approvedBy,
    });
    return response.data;
  }

  // Reject commission
  async rejectCommission(
    id: string,
    rejectedBy: string,
    rejectionReason: string
  ) {
    const response = await this.api.post(`/riders/commissions/${id}/reject`, {
      rejectedBy,
      rejectionReason,
    });
    return response.data;
  }

  // Mark commission as paid
  async markCommissionPaid(
    id: string,
    data: {
      paidBy: string;
      paymentMethod: string;
      paymentReference?: string;
      paymentDate?: string;
      paymentNotes?: string;
    }
  ) {
    const response = await this.api.post(`/riders/commissions/${id}/pay`, data);
    return response.data;
  }

  // Bulk approve commissions
  async bulkApproveCommissions(commissionIds: string[], approvedBy: string) {
    const response = await this.api.post("/riders/commissions/bulk/approve", {
      commissionIds,
      approvedBy,
    });
    return response.data;
  }

  // Bulk pay commissions
  async bulkPayCommissions(
    commissionIds: string[],
    paymentData: {
      paidBy: string;
      paymentMethod: string;
      paymentReference?: string;
      paymentDate?: string;
    }
  ) {
    const response = await this.api.post("/riders/commissions/bulk/pay", {
      commissionIds,
      ...paymentData,
    });
    return response.data;
  }

  // Trigger auto-approval
  async autoApproveCommissions(daysOld: number) {
    const response = await this.api.post(
      "/riders/commissions/system/auto-approve",
      {
        daysOld,
      }
    );
    return response.data;
  }
}

export const commissionService = new CommissionService();
export default commissionService;
